import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { AccessControlService } from '../../services';
import { ContentSkeletonComponent } from '../content-skeleton/content-skeleton.component';

export interface MultiLineValueConfig {
  topLineKey: string;
  bottomLineKeys?: string[];
  bottomLineKyes?: string[];
}

export type HeaderValueConfig = string | MultiLineValueConfig;

export const CARD_COLOR_ENUM: Record<number, string> = {
  0: '#fbe8e8',
  1: '#fff7d6',
  2: '#eef5ff',
  3: '#e8fbef',
  4: '#ffecec',
  5: '#f3edff',
  6: '#e9f7ff',
  7: '#fff1de',
  8: '#eafaf8',
  9: '#fef3f3',
  10: '#f2f4f8'
};

export interface HeaderColumn {
  headerName: string;
  valueConfig: HeaderValueConfig;
}

export interface TableHeaderAndValueKeySets {
  colorKey?: string;
  [key: string]: HeaderValueConfig | string | undefined;
}

export interface EntityListMenuAction {
  key: string;
  label: string;
  danger?: boolean;
  visibleWhen?: string;
  requiredService?: string;
}

@Component({
  selector: 'app-entity-list',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ContentSkeletonComponent],
  templateUrl: './entity-list.component.html',
  styleUrls: ['./entity-list.component.css']
})
export class EntityListComponent {
  private static readonly BADGE_TOKEN_REGEX = /\[\[badge-warning:(.+?)\]\]/i;

  @Input() list: Record<string, any>[] = [];
  @Input() loading = false;
  @Input() deletedMode = false;
  @Input() showMenuInDeletedMode = false;
  @Input() tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {};
  @Input() desktopColumnTemplate = '';
  @Input() emptyMessage = 'entityList.empty';
  @Input() menuActions: EntityListMenuAction[] = [
    { key: 'edit', label: 'action.edit' },
    { key: 'delete', label: 'action.delete', danger: true }
  ];

  @Output() cardClick = new EventEmitter<Record<string, any>>();
  @Output() editClick = new EventEmitter<Record<string, any>>();
  @Output() deleteClick = new EventEmitter<Record<string, any>>();
  @Output() menuAction = new EventEmitter<{ actionKey: string; item: Record<string, any> }>();
  @Output() loadMore = new EventEmitter<void>();
  @ViewChild('wrapperRef') private wrapperRef?: ElementRef<HTMLElement>;
  @ViewChild('listBodyRef') private listBodyRef?: ElementRef<HTMLElement>;

  activeMenuId: string | number | null = null;
  menuPanelStyle: Record<string, string> = {};
  private loadMoreQueued = false;

  constructor(private readonly access: AccessControlService) {}

  get showLoadingSkeleton(): boolean {
    return this.loading && this.list.length === 0;
  }

  get columns(): HeaderColumn[] {
    return Object.entries(this.tableHeaderAndValueKeySets)
      .filter(([headerName, valueConfig]) => headerName !== 'colorKey' && valueConfig !== undefined)
      .map(([headerName, valueConfig]) => ({
        headerName,
        valueConfig: valueConfig as HeaderValueConfig
      }));
  }

  get gridTemplateColumns(): string {
    return `${this.desktopColumnTemplate ?? ''}`.trim() || 'repeat(var(--columns-count), minmax(0, 1fr))';
  }

  trackByIndex(index: number): number {
    return index;
  }

  itemId(item: Record<string, any>, index: number): string | number {
    const value = item?.['id'];
    return value ?? index;
  }

  cardTone(index: number): string {
    return index % 2 === 0 ? 'tone-warm' : 'tone-cool';
  }

  cardBackgroundColor(item: Record<string, any>, index: number): string {
    const colorKey = this.tableHeaderAndValueKeySets['colorKey'];
    const fallback = this.cardTone(index) === 'tone-warm' ? '#fff7d6' : '#eef5ff';
    if (!colorKey) {
      return fallback;
    }

    const rawKey = item?.[colorKey];
    const enumKey = Number(rawKey);
    if (!Number.isInteger(enumKey)) {
      return fallback;
    }

    return CARD_COLOR_ENUM[enumKey] ?? fallback;
  }

  readValue(item: Record<string, any>, key: string): string {
    const value = item?.[key];
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    return `${value}`;
  }

  topLine(item: Record<string, any>, config: MultiLineValueConfig): string {
    return this.readValue(item, config.topLineKey);
  }

  bottomLine(item: Record<string, any>, config: MultiLineValueConfig): string {
    const keys = config.bottomLineKeys ?? config.bottomLineKyes ?? [];
    if (!keys.length) {
      return '';
    }

    const values = keys
      .map((key) => this.readValue(item, key))
      .filter((value) => value !== 'N/A');

    return values.join(' ');
  }

  hasBottomLineBadge(value: string): boolean {
    return EntityListComponent.BADGE_TOKEN_REGEX.test(value ?? '');
  }

  bottomLineText(value: string): string {
    return `${value ?? ''}`.replace(EntityListComponent.BADGE_TOKEN_REGEX, '').replace(/\s{2,}/g, ' ').trim();
  }

  bottomLineBadgeLabel(value: string): string {
    const match = `${value ?? ''}`.match(EntityListComponent.BADGE_TOKEN_REGEX);
    return match?.[1]?.trim() ?? '';
  }

  isMultiLineConfig(config: HeaderValueConfig): config is MultiLineValueConfig {
    return typeof config !== 'string';
  }

  toggleMenu(item: Record<string, any>, index: number, event?: MouseEvent): void {
    const id = this.itemId(item, index);
    if (this.activeMenuId === id) {
      this.closeMenu();
      return;
    }

    this.activeMenuId = id;
    this.menuPanelStyle = this.resolveMenuPanelStyle(event);
  }

  closeMenu(): void {
    this.activeMenuId = null;
    this.menuPanelStyle = {};
  }

  onCardClick(item: Record<string, any>): void {
    this.cardClick.emit(item);
    this.closeMenu();
  }

  onEdit(item: Record<string, any>): void {
    this.editClick.emit(item);
    this.closeMenu();
  }

  onDelete(item: Record<string, any>): void {
    this.deleteClick.emit(item);
    this.closeMenu();
  }

  onMenuAction(actionKey: string, item: Record<string, any>): void {
    if (actionKey === 'edit') {
      this.onEdit(item);
      return;
    }

    if (actionKey === 'delete') {
      this.onDelete(item);
      return;
    }

    this.menuAction.emit({ actionKey, item });
    this.closeMenu();
  }

  isActionVisible(action: EntityListMenuAction, item: Record<string, any>): boolean {
    if (action.requiredService && !this.access.hasService(action.requiredService)) {
      return false;
    }

    if (!action.visibleWhen) {
      return true;
    }
    const value = item?.[action.visibleWhen];
    return value !== null && value !== undefined && `${value}`.trim() !== '';
  }

  canShowMenu(): boolean {
    return !this.deletedMode || this.showMenuInDeletedMode;
  }

  onListBodyScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    this.tryLoadMore(target.scrollTop + target.clientHeight >= target.scrollHeight - 80);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeMenu();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const wrapper = this.wrapperRef?.nativeElement;
    const listBody = this.listBodyRef?.nativeElement;
    if (!wrapper || !listBody) {
      return;
    }

    const bodyHasOwnScroll = listBody.scrollHeight > listBody.clientHeight + 2;
    if (bodyHasOwnScroll) {
      return;
    }

    const rect = wrapper.getBoundingClientRect();
    const threshold = 120;
    const reachedBottom = rect.bottom <= window.innerHeight + threshold && rect.top < window.innerHeight;
    this.tryLoadMore(reachedBottom);
  }

  private resolveMenuPanelStyle(event?: MouseEvent): Record<string, string> {
    const panelWidth = 170;
    const panelHeight = 260;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const anchorRect = (event?.currentTarget as HTMLElement | null)?.getBoundingClientRect();
    const isMobile = viewportWidth <= 900;
    const viewportPadding = isMobile ? 20 : 12;
    const menuGap = isMobile ? 10 : 6;

    if (!anchorRect) {
      return {};
    }

    const left = Math.max(
      viewportPadding,
      Math.min(anchorRect.right - panelWidth, viewportWidth - panelWidth - viewportPadding)
    );
    const openUpwards = anchorRect.bottom + panelHeight > viewportHeight - viewportPadding;
    const top = openUpwards
      ? Math.max(viewportPadding, anchorRect.top - panelHeight - menuGap)
      : Math.min(viewportHeight - panelHeight - viewportPadding, anchorRect.bottom + menuGap);

    return {
      left: `${left}px`,
      top: `${top}px`
    };
  }

  private tryLoadMore(reachedBottom: boolean): void {
    if (!reachedBottom || this.loadMoreQueued) {
      return;
    }

    this.loadMoreQueued = true;
    this.loadMore.emit();
    setTimeout(() => {
      this.loadMoreQueued = false;
    }, 200);
  }
}
